import { useNavigate } from 'react-router-dom';
import AviatorDisplay from '../components/Aviator/AviatorDisplay';
import { useGetDiceInplayBetsQuery } from '../services/dice/diceServices';
import Loading from '../components/Loading';

const formatTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const AviatorLivePage = () => {
  const navigate = useNavigate();

  const { data: betsData, isLoading } = useGetDiceInplayBetsQuery('aviator', { pollingInterval: 3000 });
  const rawData = betsData?.data;
  const bets = Array.isArray(rawData)
    ? rawData
    : rawData
      ? [
          ...(rawData.pending  || []),
          ...(rawData.cashedout || []),
          ...(rawData.lost     || []),
        ]
      : [];
  const summary = betsData?.summary || {};

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-[#1a1c2e] px-5 py-3 flex items-center justify-between mb-4">
        <span className="text-white font-semibold text-base">Aviator — Live</span>
        <button
          onClick={() => navigate(-1)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors"
        >
          Back
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 px-2 items-start">
        {/* Left — Aviator Game */}
        <div className="w-full lg:w-1/2">
          <AviatorDisplay />
        </div>

        {/* Right — Bet Table */}
        <div className="w-full lg:w-1/2 bg-white rounded shadow border border-gray-200 overflow-hidden">
          <div className="bg-[#1a1c2e] px-4 py-2.5 flex items-center justify-between">
            <span className="text-white font-semibold text-sm">Current Round Bets</span>
            <span className="text-xs text-gray-400">{bets.length} bets</span>
          </div>
          {(summary.totalBets || betsData?.totalBets) ? (
            <div className="flex gap-4 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
              <span>Total Bets: <strong>{summary.totalBets ?? betsData?.totalBets ?? 0}</strong></span>
              <span>Total Stake: <strong>{summary.totalStake ?? betsData?.totalStake ?? 0}</strong></span>
              {summary.cashedout != null && <span>Cashed Out: <strong>{summary.cashedout}</strong></span>}
            </div>
          ) : null}

          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center py-8"><Loading /></div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">#</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Client</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Bet On</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Amount</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Status</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">P/L</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                        No bets for this round
                      </td>
                    </tr>
                  ) : (
                    bets.map((bet, idx) => (
                      <tr
                        key={bet._id || idx}
                        className={`border-l-4 ${
                          bet.status === 'won' ? 'border-l-green-500 bg-green-50/40'
                          : bet.status === 'lost' ? 'border-l-red-500 bg-red-50/40'
                          : 'border-l-yellow-400 bg-yellow-50/40'
                        }`}
                      >
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <div className="font-semibold text-gray-800 text-xs">{bet.fullName || bet.user?.fullName || '-'}</div>
                          {bet.userId && <div className="text-[10px] text-blue-500">{bet.userId}</div>}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 font-medium text-gray-800">{bet.betOn ?? '-'}</td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{bet.stake ?? bet.amount ?? '-'}</td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium capitalize border ${
                            bet.status === 'won' ? 'bg-green-100 text-green-700 border-green-300'
                            : bet.status === 'lost' ? 'bg-red-100 text-red-700 border-red-300'
                            : 'bg-yellow-100 text-yellow-700 border-yellow-300'
                          }`}>
                            {bet.status || 'pending'}
                          </span>
                        </td>
                        <td className={`px-3 py-2 border-b border-gray-100 font-semibold text-xs ${(bet.pl || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {bet.pl != null ? (bet.pl >= 0 ? '+' : '') + Number(bet.pl).toFixed(2) : '-'}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-400 text-xs whitespace-nowrap">
                          {formatTime(bet.placedAt || bet.createdAt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AviatorLivePage;
